// frontend/src/components/Booking/BookingList.tsx
// UPDATED: Fixed to show actual service names and proper provider details

import React from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Calendar, 
  MapPin, 
  User, 
  Building2, 
  Package, 
  Phone, 
  Mail,
  Clock,
  CheckCircle,
  XCircle,
  AlertCircle
} from 'lucide-react';
import { Booking, BookingStatus } from '@/types/booking';

interface BookingListProps {
  bookings: Booking[];
  currentUserRole: 'admin' | 'provider' | 'customer';
  isLoading?: boolean;
  emptyMessage?: string;
  onAction?: (action: string, bookingId: number) => void;
}

// Status configuration
const getStatusConfig = (status: BookingStatus) => {
  switch (status) {
    case 'pending':
      return { 
        icon: Clock, 
        color: 'text-yellow-600', 
        bg: 'bg-yellow-100',
        label: 'Pending'
      };
    case 'accepted':
      return { 
        icon: CheckCircle, 
        color: 'text-blue-600', 
        bg: 'bg-blue-100',
        label: 'Accepted'
      };
    case 'completed':
      return { 
        icon: CheckCircle, 
        color: 'text-green-600', 
        bg: 'bg-green-100',
        label: 'Completed'
      };
    case 'rejected':
      return { 
        icon: XCircle, 
        color: 'text-red-600', 
        bg: 'bg-red-100',
        label: 'Rejected'
      };
    case 'cancelled':
      return { 
        icon: XCircle, 
        color: 'text-red-600', 
        bg: 'bg-red-100',
        label: 'Cancelled'
      };
    default:
      return { 
        icon: AlertCircle, 
        color: 'text-gray-600', 
        bg: 'bg-gray-100',
        label: status
      };
  }
};

const formatDate = (dateString: string) => {
  return new Date(dateString).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric'
  });
};

const formatTime = (dateString: string) => {
  return new Date(dateString).toLocaleTimeString('en-US', {
    hour: '2-digit',
    minute: '2-digit'
  });
};

const BookingList: React.FC<BookingListProps> = ({
  bookings,
  currentUserRole,
  isLoading,
  emptyMessage = "No bookings found.",
  onAction
}) => {
  const navigate = useNavigate();

  const handleBookingClick = (booking: Booking) => {
    if (booking.booking_id) {
      const basePath = currentUserRole === 'customer' 
        ? '/account/bookings' 
        : '/bookings';
      navigate(`${basePath}/${booking.booking_id}`);
    }
  };

  // Loading state
  if (isLoading) {
    return (
      <div className="p-6">
        <div className="space-y-4">
          {[...Array(3)].map((_, i) => (
            <div key={i} className="animate-pulse">
              <div className="bg-gray-200 rounded-lg h-24"></div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  // Empty state
  if (!bookings || bookings.length === 0) {
    return (
      <div className="p-8 text-center">
        <Package className="h-12 w-12 text-gray-400 mx-auto mb-4" />
        <h3 className="text-lg font-medium text-gray-900 mb-2">No Bookings Found</h3>
        <p className="text-gray-600 max-w-md mx-auto">{emptyMessage}</p>
      </div>
    );
  }

  return (
    <div className="divide-y divide-gray-200">
      {bookings.map((booking) => {
        const statusConfig = getStatusConfig(booking.status);
        const StatusIcon = statusConfig.icon;

        return (
          <div
            key={booking.booking_id}
            onClick={() => handleBookingClick(booking)}
            className="p-4 hover:bg-gray-50 cursor-pointer transition-colors"
          >
            <div className="flex items-start justify-between">
              {/* Main Content */}
              <div className="flex-1 min-w-0">
                {/* Header Row */}
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center space-x-2">
                    {/* Booking ID */}
                    <span className="text-sm font-medium text-gray-900">
                      #{booking.booking_id}
                    </span>
                    
                    {/* Status Badge */}
                    <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${statusConfig.bg} ${statusConfig.color}`}>
                      <StatusIcon className="h-3 w-3 mr-1" />
                      {statusConfig.label}
                    </span>
                  </div>
                  
                  <span className="text-xs text-gray-500">
                    {formatDate(booking.created_at)} at {formatTime(booking.created_at)}
                  </span>
                </div>

                {/* Service Information - Show actual service name */}
                <div className="mb-2">
                  <div className="flex items-center space-x-2">
                    <Package className="h-4 w-4 text-gray-400 flex-shrink-0" />
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-medium text-gray-900 truncate">
                        {booking.service_name || 'Service Request'}
                      </p>
                      {booking.service_price && (
                        <p className="text-xs text-gray-500">
                          {booking.service_price}
                        </p>
                      )}
                    </div>
                  </div>
                </div>

                {/* Date and Location */}
                <div className="space-y-1 mb-2">
                  <div className="flex items-center space-x-2">
                    <Calendar className="h-4 w-4 text-gray-400 flex-shrink-0" />
                    <span className="text-sm text-gray-600">
                      {formatDate(booking.service_date)}
                      {booking.preferred_time && (
                        <span className="text-gray-500">
                          at {booking.preferred_time}
                        </span>
                      )}
                    </span>
                  </div>
                  
                  {booking.location && (
                    <div className="flex items-start space-x-2">
                      <MapPin className="h-4 w-4 text-gray-400 mt-0.5 flex-shrink-0" />
                      <span className="text-sm text-gray-600 line-clamp-1">
                        {booking.location}
                      </span>
                    </div>
                  )}
                </div>

                {/* Customer/Provider Info based on role */}
                <div className="flex items-center space-x-4">
                  {/* Customer Info - Show for admin and provider only */}
                  {currentUserRole !== 'customer' && (
                    <div className="flex items-center space-x-2">
                      <User className="h-4 w-4 text-gray-400 flex-shrink-0" />
                      <div className="min-w-0">
                        <p className="text-sm font-medium text-gray-900 truncate">
                          {booking.customer_name || 'Customer'}
                        </p>
                        {booking.customer_email && (
                          <p className="text-xs text-gray-600 truncate">
                            {booking.customer_email}
                          </p>
                        )}
                        {booking.customer_phone && (
                          <p className="text-xs text-gray-600">
                            {booking.customer_phone}
                          </p>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Provider Info - Show for admin and customer only */}
                  {currentUserRole !== 'provider' && (
                    <div className="flex items-center space-x-2">
                      <Building2 className="h-4 w-4 text-gray-400 flex-shrink-0" />
                      <div className="min-w-0">
                        <p className="text-sm font-medium text-gray-900 truncate">
                          {booking.business_name || booking.provider_name || 'Service Provider'}
                        </p>
                        {booking.contact_person && (
                          <p className="text-xs text-gray-600 truncate">
                            Contact: {booking.contact_person}
                          </p>
                        )}
                        {(booking.contact_phone || booking.provider_phone) && (
                          <p className="text-xs text-gray-600 flex items-center">
                            <Phone className="h-3 w-3 mr-1" />
                            {booking.contact_phone || booking.provider_phone}
                          </p>
                        )}
                        {(booking.contact_email || booking.provider_email) && (
                          <p className="text-xs text-gray-600 flex items-center">
                            <Mail className="h-3 w-3 mr-1" />
                            {booking.contact_email || booking.provider_email}
                          </p>
                        )}
                      </div>
                    </div>
                  )}
                </div>

                {/* Description Preview */}
                {booking.description && (
                  <div className="mt-2">
                    <p className="text-sm text-gray-600 line-clamp-2">
                      {booking.description}
                    </p>
                  </div>
                )}
              </div>

              {/* Actions for Provider */}
              {currentUserRole === 'provider' && booking.status === 'pending' && onAction && (
                <div className="flex flex-col space-y-2 ml-4">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      onAction('accept', booking.booking_id);
                    }}
                    className="text-xs px-3 py-1 bg-green-100 text-green-700 rounded hover:bg-green-200 transition-colors"
                  >
                    Accept
                  </button>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      onAction('reject', booking.booking_id);
                    }}
                    className="text-xs px-3 py-1 bg-red-100 text-red-700 rounded hover:bg-red-200 transition-colors"
                  >
                    Reject
                  </button>
                </div>
              )}

              {/* Complete Action for Accepted Bookings */}
              {currentUserRole === 'provider' && booking.status === 'accepted' && onAction && (
                <div className="ml-4">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      onAction('complete', booking.booking_id);
                    }}
                    className="text-xs px-3 py-1 bg-blue-100 text-blue-700 rounded hover:bg-blue-200 transition-colors"
                  >
                    Mark Complete
                  </button>
                </div>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default BookingList;